import { AuthedButton } from '../authed-button';
import { DrawerDiaLog, DrawerDiaLogTitle } from '../drawer-dialog';
import { InteractCopyIcon } from '@/client/assets';
import { FAUCET_CLAIM_AMOUNT, IS_DEBUG } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { useTradeGate } from '@/client/hooks/use-trade-gate';
import { useWallet } from '@/client/hooks/use-wallet';
import { useAppSettingStore } from '@/client/store/app-store';
import { getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { publicClient } from '@/client/utils/chain-client';
import { refetchQuery } from '@/client/utils/query-status';
import { isBoundWalletMismatch, resolveWalletAddress, truncateWallet } from '@/client/utils/wallet-address';
import { testTokenAbi } from '@/libs/sdk/contracts/abis/test-token';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { parseUnits, type Address } from 'viem';

type FaucetModelProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const FaucetModel = ({ open, setOpen }: FaucetModelProps) => {
  const { t } = useTranslation();
  const { linkedWallet } = useAuth();
  const { data: singer } = useSignedUserInfo(api);
  const { walletClient } = useWallet();
  const tradeGate = useTradeGate();
  const baseToken = useAppSettingStore(state => state.baseToken);
  // Same rule as the deposit panel: only the PROVEN wallet may render as a
  // receive address (singer.address can be a DID identity key).
  const walletAddress = resolveWalletAddress(linkedWallet, singer?.address);
  const [isClaiming, setIsClaiming] = useState(false);
  const tokenLabel = t(baseToken.translateKey);

  const handleCopyAddress = useCallback(() => {
    if (!walletAddress) return;
    navigator.clipboard.writeText(walletAddress);
    toast.success(t('address-copied'));
  }, [walletAddress, t]);

  const handleClaim = async () => {
    if (isClaiming) return;
    // Fail-closed funnel (ADR-051 D2): every chain write goes through the trade
    // gate — it blocks while the wallet binding is unproven and routes the user
    // to login/onboarding instead of silently no-oping.
    if (!tradeGate()) return;
    if (!walletClient?.account) return;
    if (isBoundWalletMismatch(linkedWallet, walletClient.account.address)) {
      toast.error(
        t('wrong-wallet', 'Wrong wallet connected. Switch to your linked wallet {{wallet}} to trade.', {
          wallet: truncateWallet(linkedWallet),
        })
      );
      return;
    }
    setIsClaiming(true);
    try {
      const claimArgs = {
        address: baseToken.address as Address,
        abi: testTokenAbi,
        functionName: 'faucet' as const,
        args: [parseUnits(String(FAUCET_CLAIM_AMOUNT), baseToken.decimals)] as const,
        account: walletClient.account,
      };
      const claimGas = await publicClient.estimateContractGas(claimArgs);
      const txHash = await walletClient.writeContract({
        ...claimArgs,
        chain: walletClient.chain,
        gas: (claimGas * 130n) / 100n,
      });
      await publicClient.waitForTransactionReceipt({ hash: txHash });
      toast.success(t('faucet-success', { amount: FAUCET_CLAIM_AMOUNT, token: tokenLabel }));
      await refetchQuery('SingerTokenBalance');
    } catch (error) {
      if (IS_DEBUG) console.log('faucet claim error', error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <DrawerDiaLog open={open} setOpen={setOpen} className="bg-card px-4 pb-4 md:p-6 w-full max-w-[420px]">
      <div className="space-y-4">
        <DrawerDiaLogTitle className="font-semibold text-xl leading-6">{t('testnet-faucet')}</DrawerDiaLogTitle>
        <p className="text-sm leading-4.5 text-foreground/70">{t('faucet-provided-note')}</p>
        {walletAddress && (
          <div className="w-full rounded-4.5 p-.5 bg-foreground/4">
            <div className="p-3 rounded-4 font-semibold text-sm leading-none border border-foreground/10 bg-card break-all">
              {walletAddress}
            </div>
            <div
              onClick={handleCopyAddress}
              className="flex items-center justify-center px-4 py-2.5 gap-1 cursor-pointer text-foreground hover:text-beneficial"
            >
              <InteractCopyIcon className="size-5 fill-current" />
              <span className="font-medium">{t('copy-address')}</span>
            </div>
          </div>
        )}
        <AuthedButton
          className="w-full h-auto p-3.5 font-medium rounded-3"
          isLoading={isClaiming}
          onClick={handleClaim}
        >
          {t('faucet-get-token', { amount: FAUCET_CLAIM_AMOUNT, token: tokenLabel })}
        </AuthedButton>
        <p className="text-center text-xs leading-[normal] text-foreground/40">{t('faucet-gas-note')}</p>
      </div>
    </DrawerDiaLog>
  );
};
